/**
 * The assistant conversation's pure rules: what a turn sends (history, screen context, drafts on
 * screen) and how a reply lands (thread, views, drafts, what the panel shows). Kept out of the
 * provider so they are tested without rendering.
 */
import type {
  AssistantAttachmentInput,
  AssistantChoice,
  AssistantDraftContext,
  AssistantHistoryMessage,
  AssistantReply,
  AssistantScreenContext,
  AssistantTurnRequest,
} from '../../types/assistant';
import { ASSISTANT_MAX_HISTORY, ASSISTANT_MAX_MESSAGE_LENGTH } from '../../services/assistant';
import type { AssistantMessage, AssistantPrimary, OpenDraft, ScreenIds, ShownView } from './assistantContext';
import { getDraftHandler, readAssistantDraft } from './draftRegistry';
import { assistantViewContextIds } from './viewRegistry';

/** The API keeps this many ids from the screen; sending more is wasted bytes. */
export const MAX_CONTEXT_IDS = 50;

/** Drafts on screen the API reads. */
export const MAX_CONTEXT_DRAFTS = 3;

/**
 * A message as history tells it. A user message with files says how many, never their names (the files went with
 * their own turn only), so a files-only message still reaches the model as "(1 file attached)".
 */
export const historyText = (message: Pick<AssistantMessage, 'role' | 'text' | 'attachments'>): string => {
  const count = message.role === 'user' ? (message.attachments?.length ?? 0) : 0;
  if (count === 0) return message.text;
  const text = message.text.trim();
  const note = `(${count} ${count === 1 ? 'file' : 'files'} attached)`;
  return text ? `${text} ${note}` : note;
};

export const historyOf = (messages: readonly AssistantMessage[]): AssistantHistoryMessage[] =>
  messages
    .filter((m) => !m.failed && !m.local)
    .map((m) => ({ role: m.role, text: historyText(m) }))
    .filter((m) => m.text.trim() !== '')
    .slice(-ASSISTANT_MAX_HISTORY)
    .map((m) => ({ role: m.role, text: m.text.slice(0, ASSISTANT_MAX_MESSAGE_LENGTH) }));

/** The API reads screen ids as GUIDs: one that is not (a hand-edited URL) would fail the whole turn, so it is dropped. */
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** A kind the API echoes to the model: a short lowercase word. Anything else would be ignored there anyway. */
const KIND = /^[a-z_]{1,32}$/;

/** Adds `extra` into `into`, kind by kind, without repeats. */
const mergeIds = (into: Map<string, string[]>, extra: ScreenIds) => {
  for (const [kind, ids] of Object.entries(extra)) {
    if (!ids || !KIND.test(kind)) continue;
    const list = into.get(kind) ?? [];
    for (const id of ids) if (typeof id === 'string' && GUID.test(id) && !list.includes(id)) list.push(id);
    into.set(kind, list);
  }
};

/**
 * What the coach is looking at: the route, then ids from the panel's view first (what the coach
 * just read), then the page's own, then a tapped answer's target, cut to the API's limit in that
 * order of priority so the most relevant survive.
 */
export const screenContextOf = (
  route: string,
  primaryView: ShownView | null,
  pageIds: readonly ScreenIds[],
  choice?: AssistantChoice
): AssistantScreenContext => {
  const merged = new Map<string, string[]>();
  if (choice?.targetKind && choice.targetId) mergeIds(merged, { [choice.targetKind]: [choice.targetId] });
  if (primaryView) mergeIds(merged, assistantViewContextIds(primaryView.view));
  for (const ids of pageIds) mergeIds(merged, ids);

  let budget = MAX_CONTEXT_IDS;
  const ids: ScreenIds = {};
  for (const [kind, list] of merged) {
    if (budget <= 0) break;
    const kept = list.slice(0, budget);
    budget -= kept.length;
    if (kept.length > 0) ids[kind] = kept;
  }
  return { route, ids };
};

/** Drafts on screen, as each handler describes them (model-safe facts only). Kinds with no `toContext` say only that they are open. */
export const draftsContextOf = (drafts: readonly OpenDraft[]): AssistantDraftContext[] =>
  drafts.slice(-MAX_CONTEXT_DRAFTS).map(({ draft }) => {
    const handler = getDraftHandler(draft.kind);
    // The same read the component and the confirm get; an unreadable draft tells the model only that it is open.
    const readable = readAssistantDraft(draft);
    let payload: unknown = null;
    try {
      payload = handler?.toContext && readable ? handler.toContext(readable) : null;
    } catch {
      payload = null;
    }
    return { kind: draft.kind, payload };
  });

/** The coach's own date and zone, from the browser (the only side that knows them). */
export const clientClock = (now: Date = new Date()): { localDate: string; timeZone: string | null } => {
  const pad = (n: number) => String(n).padStart(2, '0');
  let timeZone: string | null = null;
  try {
    timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone ?? null;
  } catch {
    timeZone = null;
  }
  return { localDate: `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`, timeZone };
};

export const buildTurnRequest = (
  text: string,
  messages: readonly AssistantMessage[],
  context: AssistantScreenContext,
  drafts: readonly OpenDraft[],
  now: Date = new Date(),
  attachments: readonly AssistantAttachmentInput[] = []
): AssistantTurnRequest => ({
  message: text.slice(0, ASSISTANT_MAX_MESSAGE_LENGTH),
  history: historyOf(messages),
  context,
  drafts: draftsContextOf(drafts),
  ...clientClock(now),
  // This turn's files only: the history above is text, so a later turn never sends them again.
  ...(attachments.length > 0 ? { attachments: attachments.map(({ name, data }) => ({ name, data })) } : {}),
});

export interface LandedReply {
  message: AssistantMessage;
  views: ShownView[];
  drafts: OpenDraft[];
  primary: AssistantPrimary;
  /** False when the answer shows nothing new (a question back): the panel stays as it was. */
  changesPrimary: boolean;
}

/**
 * Lands one reply. Views and drafts get keys unique across turns. A new draft of a kind already on
 * screen replaces it (one board draft at a time), as the API tells the model.
 */
export const landReply = (
  reply: AssistantReply,
  turn: number,
  messageId: string,
  openDrafts: readonly OpenDraft[]
): LandedReply => {
  const views: ShownView[] = reply.views.map((view, index) => ({ key: `t${turn}-${view.id || `v${index + 1}`}`, view }));
  const newDrafts: OpenDraft[] = reply.drafts.map((draft, index) => ({
    key: `t${turn}-${draft.id || `d${index + 1}`}`,
    draft,
    saving: false,
    error: null,
  }));
  const replacedKinds = new Set(newDrafts.map((d) => d.draft.kind));
  const drafts = [...openDrafts.filter((d) => d.saving || !replacedKinds.has(d.draft.kind)), ...newDrafts];

  let primary: AssistantPrimary = null;
  if (reply.primaryDraft != null && newDrafts[reply.primaryDraft]) primary = { type: 'draft', key: newDrafts[reply.primaryDraft].key };
  else if (reply.primaryView != null && views[reply.primaryView]) primary = { type: 'view', key: views[reply.primaryView].key };

  return {
    message: {
      id: messageId,
      role: 'assistant',
      text: reply.message ?? '',
      viewKeys: views.map((v) => v.key),
      draftKeys: newDrafts.map((d) => d.key),
      suggestions: reply.suggestions,
      question: reply.question ?? null,
    },
    views,
    drafts,
    primary,
    changesPrimary: primary !== null,
  };
};
